import { executeRecaptcha } from '@vo/js/modules/recaptcha'
import validateEmail from '@vo/js/utils/validateEmail'
import { __ } from '@vo/js/utils/i18n'
import { gtmHandleFormSubmit, gtmHandleFormError } from '@vo/js/modules/tagManagerEvents'
import { popupHideIfSubmitted, setPopupSubmitted } from '@vo/js/utils/newsletter'

/**
 * Handle ajax form submission success
 * @param {HTMLFormElement} form
 * @private
 */
const _handleAjaxFormSuccess = (form) => {
    const successNotice = form.querySelector('[data-form-success]')
    if (successNotice) {
        successNotice.classList.remove('hidden')
        form.querySelector('[data-form-wrapper]')?.remove()
    }

    const formType = form.getAttribute('data-form-type')
    if (formType === 'newsletter-popup' && popupHideIfSubmitted()) {
        setPopupSubmitted()
    }

    gtmHandleFormSubmit(form)
}

/**
 * Handle ajax form submission error
 * @param {HTMLFormElement} form
 * @param {Object} data
 * @private
 */
const _handleAjaxFormError = (form, data) => {
    const errorNotice = form.querySelector('[data-form-error]')
    if (errorNotice) {
        let error = data.error || ''
        const errors = data.errors || []

        if (errors.length) {
            error = ''
            errors.forEach((err) => error += `${err}<br>`)
        }

        errorNotice.innerHTML = error
        errorNotice.classList.remove('hidden')
        gtmHandleFormError(form, errors)
    }

    form.querySelector('button[type=submit]')?.removeAttribute('disabled')
}

/**
 * Handle ajax form submission
 * @param {HTMLFormElement} form
 * @returns {Promise<void>}
 * @private
 */
const _submitAjaxForm = async (form) => {
    const formData = new FormData(form)

    try {
        const res = await axios.request({
            method: form.getAttribute('method') || 'post',
            url: form.getAttribute('action') || window.location.href,
            data: formData,
            headers: {
                'Cache-Control': 'no-cache'
            }
        })

        const data = res.data

        if (data.success) {
            _handleAjaxFormSuccess(form)
        } else {
            _handleAjaxFormError(form, data)
        }
    } catch (err) {
        _handleAjaxFormError(form, err.response.data)
    }
}

/**
 * Submit form
 * @param {HTMLFormElement} form
 */
export const submitForm = (form) => {
    if (!form) {
        return
    }

    if (form.hasAttribute('data-form-ajax')) {
        _submitAjaxForm(form)
    } else {
        form.submit()
    }
}

//** Form input methods **//

/**
 * Set up form events and debounce
 */
const setupForms = () => {
    const forms = document.querySelectorAll('form:not([data-nitro-form]):not([data-nitro-fragment]):not([data-cordial-form])')

    /**
     * Debounce form submissions to prevent double submissions.
     * @param form
     */
    const debounceForm = (form) => {
        let debounce

        form.addEventListener('submit', (e) => {
            e.preventDefault()

            if (debounce) {
                clearTimeout(debounce)
            }

            debounce = setTimeout(() => {
                form.querySelector('button[type=submit]')?.setAttribute('disabled', 'disabled')

                if (form.querySelector('[data-recaptcha]') && document.querySelector('.g-recaptcha')) {
                    executeRecaptcha(form)
                } else {
                    submitForm(form)
                }
            }, 100)
        })
    }

    const handleForms = () => {
        if (!forms) return

        forms.forEach((form) => {
            debounceForm(form)
        })
    }

    const init = () => {
        handleForms()
    }

    init()
}

/**
 * Get a human-readable label for a form field
 * @param {Element} field
 * @returns {String}
 */
const getFieldLabel = (field) => {
    const label = field.closest('.input-field')?.querySelector('.input-label')
    const labelText = label?.textContent
        ?.replace(/\(required\)/gi, '')
        ?.trim()

    return labelText || field.getAttribute('name') || __('this field')
}

/**
 * Get selectable option labels from a select field
 * @param {HTMLSelectElement} field
 * @returns {String[]}
 */
const getSelectOptionLabels = (field) => {
    return [...field.options]
        .filter((option) => option.value && !option.disabled)
        .map((option) => option.textContent.trim())
        .filter(Boolean)
}

/**
 * Build a corrective error suggestion for a required/empty field
 * @param {Element} field
 * @returns {String}
 */
const getRequiredSuggestion = (field) => {
    const type = (field.getAttribute('type') || field.tagName).toLowerCase()
    const label = getFieldLabel(field)

    if (type === 'email') {
        return __('Enter your email in the format: name@domain.com')
    }

    if (type === 'tel') {
        return __('Enter a phone number, for example: +1 555 123 4567')
    }

    if (field.tagName === 'SELECT') {
        const options = getSelectOptionLabels(field)

        if (options.length && options.length <= 12) {
            return __('Choose one of: :options', { options: options.join(', ') })
        }

        return __('Choose an option from the :field list.', { field: label })
    }

    return __('Enter a value for :field.', { field: label })
}

/**
 * Check if required fields have a value
 */
const required = () => {
    const requiredFields = document.querySelectorAll('input[required], textarea[required], select[required]')

    requiredFields.forEach((field) => {
        field.addEventListener('focusout', () => {
            if (!field.value || !field.value.trim().length) {
                if (field.tagName !== 'SELECT') {
                    field.value = ''
                }
                error(field, true, getRequiredSuggestion(field))
            } else {
                error(field, false)
            }
        })
    })
}

/**
 * Validate specific fields like email
 */
const validation = () => {
    const validationFields = document.querySelectorAll('input[type="email"]')

    validationFields.forEach((field) => {
        field.addEventListener('focusout', () => {
            if (!field.value || !field.value.trim().length) {
                return
            }

            error(
                field,
                !validateEmail(field.value),
                __('Enter your email in the format: name@domain.com')
            )
        })
    })
}

/**
 * Handle error state and message
 * @param {Element} field
 * @param {null|Boolean} error
 * @param {String} message
 */
const error = (field = null, error = false, message = '') => {
    if (!field) {
        return
    }

    const validationMessage = field.closest('.input-field')?.querySelector('.input-validation-message')

    if (error) {
        field.classList.add('error')
        field.setAttribute('aria-invalid', 'true')

        if (message && validationMessage) {
            validationMessage.textContent = message

            if (validationMessage.id) {
                const describedBy = new Set(
                    (field.getAttribute('aria-describedby') || '')
                        .split(/\s+/)
                        .filter(Boolean)
                )
                describedBy.add(validationMessage.id)
                field.setAttribute('aria-describedby', [...describedBy].join(' '))
            }
        }
    } else {
        field.classList.remove('error')
        field.setAttribute('aria-invalid', 'false')

        if (validationMessage) {
            validationMessage.textContent = ''
        }
    }
}

/**
 * Move focus to the first invalid field after a failed server-side submit
 */
const focusFirstInvalidField = () => {
    const firstInvalidField = document.querySelector(
        '.input-field [aria-invalid="true"], .input-field input.error, .input-field textarea.error, .input-field select.error'
    )

    firstInvalidField?.focus()
}

/**
 * Handle focus state
 */
const focus = () => {
    const fields = document.querySelectorAll('.input-field input, .input-field textarea')

    fields.forEach((field) => {
        field.addEventListener('focusin', () => {
            field.closest('.input-field').classList.add('focused')
        })

        field.addEventListener('focusout', () => {
            field.closest('.input-field').classList.remove('focused')
        })
    })
}

/**
 * Handle filled state
 */
const filled = () => {
    const fields = document.querySelectorAll('.input-field input, .input-field textarea')

    fields.forEach((field) => {
        if (field.value) {
            field.closest('.input-field').classList.add('filled')
        } else {
            field.closest('.input-field').classList.remove('filled')
        }

        field.addEventListener('focusout', () => {
            if (field.value) {
                field.closest('.input-field').classList.add('filled')
            } else {
                field.closest('.input-field').classList.remove('filled')
            }
        })

        field.addEventListener('change', () => {
            if (field.value) {
                field.closest('.input-field').classList.add('filled')
            } else {
                field.closest('.input-field').classList.remove('filled')
            }
        })
    })
}

export default (function () {
    setupForms()
    required()
    validation()
    focus()
    filled()
    focusFirstInvalidField()
})
